import { Allergen, CerealSubAllergen, Dietary, NutSubAllergen } from '@/generated/gql/graphql'
import { numberFormat } from '@/helpers/numbers'
import { DietaryRow } from '@fusion/dietary/row'
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined'
import {
  Button,
  CardActions,
  CardContent,
  CardHeader,
  Grid,
  Stack,
  Typography,
} from '@mui/material'

import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { optionsAreValid } from '../ccMenuSpecificHelpers'
import ItemCardV2 from './shared/ItemCardV2'

type CCMenuItemCardItemData = {
  submenuId: string
  quantityRemaining: number | null
  liveVariation: {
    priceCustomerFacing: number | null
    varType: string | null
    orderLimit: number | null
  }
  liveMenuItem: {
    _id: string
    name: string | null
    image: string | null
    dietaries: Dietary[]
    allergens: Allergen[]
    isCombo: boolean | null
    shortDescription: string | null
    subAllergens: {
      nuts: NutSubAllergen[]
      cereals: CerealSubAllergen[]
    }
    options:
      | ({
          _id: string
          minQty: number | null
          maxQty: number | null
          qtyPerPerson: number | null
        } | null)[]
      | null
  }
}

type Props = {
  itemData: CCMenuItemCardItemData
  vendorLogo: string | null | undefined
  vendorCompanyName: string
  hidePrices: boolean
  onSeeMoreClick: (itemId: string) => void
  onAddToCart: (itemId: string) => void
  disableAddToCart?: boolean
}

const CCMenuItemCardV2: React.FC<Props> = ({
  hidePrices,
  itemData,
  vendorLogo,
  vendorCompanyName,
  onSeeMoreClick,
  onAddToCart,
  disableAddToCart = false,
}) => {
  const { liveMenuItem, quantityRemaining, liveVariation: ccVariation } = itemData
  const { t } = useTranslation()

  const soldOut = quantityRemaining === 0

  const handleSeeMoreClick = useCallback(() => {
    if (soldOut) return
    onSeeMoreClick(liveMenuItem._id)
  }, [onSeeMoreClick, liveMenuItem._id])

  const handleAddToCart = useCallback(() => {
    if (soldOut) return
    if (liveMenuItem.options && optionsAreValid(liveMenuItem.options, {})) {
      onAddToCart(liveMenuItem._id)
    } else {
      onSeeMoreClick(liveMenuItem._id)
    }
  }, [onAddToCart, liveMenuItem, handleSeeMoreClick])

  const formattedPrice = useMemo(() => {
    return numberFormat((ccVariation?.priceCustomerFacing || 0) / 100)
  }, [ccVariation?.priceCustomerFacing])

  return (
    <ItemCardV2
      vendorAvatar={vendorLogo}
      vendorName={vendorCompanyName}
      imageUrl={liveMenuItem.image}
      imageTitle={liveMenuItem.name}
      onClick={handleSeeMoreClick}
      quantityRemaining={quantityRemaining}>
      <Stack justifyContent="space-between" height="100%">
        <CardHeader title={liveMenuItem.name} sx={{ padding: '8px 0 0' }} />
        <CardContent
          sx={{ padding: 0, lineHeight: 'normal', '&:last-child': { paddingBottom: 0 } }}>
          <Stack display="flex" justifyContent="space-between" gap={2}>
            <Typography variant="body3" sx={{ color: 'text.secondary' }} lineHeight={1.5}>
              {liveMenuItem.shortDescription}
            </Typography>
            <DietaryRow
              isComboItem={Boolean(liveMenuItem.isCombo)}
              maxToShow={4}
              subAllergens={liveMenuItem.subAllergens}
              allergens={liveMenuItem.allergens}
              dietaries={liveMenuItem.dietaries}
            />
          </Stack>
        </CardContent>
        <CardActions onClick={(e) => e.stopPropagation()} sx={{ padding: '8px 0 0' }}>
          <Stack width="100%" direction="row" alignItems="center" justifyContent="space-between">
            {!hidePrices ? (
              <Typography
                variant="body1"
                sx={{ fontWeight: '600' }}>{`${formattedPrice} Credits`}</Typography>
            ) : null}
            <Grid container spacing={0.5}>
              <Button
                onClick={handleSeeMoreClick}
                size="medium"
                variant="outlined"
                color={soldOut ? 'error' : 'primary'}
                sx={{
                  fontSize: 12,
                  borderWidth: 1.5,
                  padding: '6px',
                }}>
                {soldOut
                  ? t('cc:soldOut')
                  : liveMenuItem.options && liveMenuItem.options.length > 0
                    ? t('cc:showOptions')
                    : t('cc:showMore')}
              </Button>
              <Button
                onClick={handleAddToCart}
                variant="contained"
                size="medium"
                sx={{ minWidth: 40 }}
                disabled={
                  disableAddToCart ||
                  (typeof quantityRemaining === 'number' && quantityRemaining <= 0)
                }>
                <ShoppingCartOutlinedIcon
                  sx={{
                    width: 20,
                    height: 20,
                  }}
                />
              </Button>
            </Grid>
          </Stack>
        </CardActions>
      </Stack>
    </ItemCardV2>
  )
}

export default React.memo(CCMenuItemCardV2, (prev, next) => {
  return (
    prev.hidePrices === next.hidePrices &&
    prev.vendorCompanyName === next.vendorCompanyName &&
    prev.vendorLogo === next.vendorLogo &&
    prev.itemData.quantityRemaining === next.itemData.quantityRemaining &&
    prev.itemData.liveMenuItem === next.itemData.liveMenuItem &&
    prev.disableAddToCart === next.disableAddToCart
  )
})
